import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HandHeart, Building2, Users, Heart, ArrowRight, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../context/LanguageContext";

const COPY = {
  en: {
    overline: "Funders & Partners",
    title: "Help Move The Pillar Project From Referrals to Direct Services.",
    subtitle: "Today, we help survivors navigate and access services through direct advocacy and trusted referral relationships. With expanded funding, we can build internal capacity to provide housing, legal, mental health, and hospital-based advocacy services directly.",
    prioritiesTitle: "What Funding Makes Possible",
    prioritiesIntro: "Today, The Pillar Project helps survivors navigate and access services through direct advocacy and trusted referral relationships. With expanded funding, we can increase emergency assistance, build internal professional capacity, create coordinated workforce programs, and ultimately provide housing, legal, mental health, and hospital-based advocacy services directly.",
    priorities: [
      { label: "Emergency Survivor Assistance", detail: "Transportation, essential items, replacement documents, work-appropriate clothing." },
      { label: "Workforce Development", detail: "Job readiness, hospitality/ServSafe pathways, professional coaching, employer connections." },
      { label: "Community Resource Fairs", detail: "Connecting survivors and community members with vetted providers in one place." },
      { label: "Transportation", detail: "Rides to shelter, court, employment, training, and essential services." },
      { label: "Temporary Emergency Lodging", detail: "When shelter beds are unavailable and funds allow — approved case-by-case." },
      { label: "Staff & Case-Management Capacity", detail: "So we can stay with survivors through referrals, waiting lists, and appointments." },
      { label: "In-House Mental Health Services", detail: "Bringing trauma-informed counseling directly into The Pillar Project." },
      { label: "In-House Legal Services", detail: "Survivor-centered attorneys for protective orders, family law, trafficking, and sexual-assault matters." },
      { label: "Professional Training", detail: "Trauma-informed education for police, hospitals, and community groups across Maryland." },
      { label: "Hospital Advocacy Development", detail: "Building the model for trained advocates at hospitals to intervene at the point of care." },
      { label: "Data & Impact Measurement", detail: "Rigorous outcome tracking so funders and partners see the difference their support makes." },
      { label: "Transitional Housing & Shelter Development", detail: "Building The Pillar Project's own housing capacity over time." },
    ],
    waysTitle: "Ways to Partner",
    ways: [
      { title: "Grant Partnership", desc: "Multi-year investments in workforce, hospital advocacy, or integrated services." },
      { title: "Corporate Sponsorship", desc: "Underwrite trainings, resource fairs, or workforce cohorts." },
      { title: "Emergency Survivor Fund", desc: "Direct contributions for transportation, lodging, and essential items." },
      { title: "Program Sponsorship", desc: "Sponsor a training series, workforce cohort, or resource-fair event." },
    ],
    ctaTitle: "Let's Talk About What's Possible.",
    ctaText: "Grant partnerships, corporate sponsorships, in-kind support, and multi-year commitments all move The Pillar Project forward.",
    ctaSponsorship: "Request Sponsorship Package",
    ctaGrant: "Discuss a Grant Partnership",
    ctaDonate: "Donate Now",
  },
  es: {
    overline: "Financiadores y Socios",
    title: "Ayuda a The Pillar Project a Avanzar de Referencias a Servicios Directos.",
    subtitle: "Hoy, ayudamos a los sobrevivientes a navegar y acceder a servicios a través de defensa directa y relaciones de referencia de confianza. Con financiamiento ampliado, podemos construir capacidad interna para ofrecer servicios de vivienda, legales, de salud mental y de defensa hospitalaria directamente.",
    prioritiesTitle: "Lo Que el Financiamiento Hace Posible",
    prioritiesIntro: "Hoy, The Pillar Project ayuda a los sobrevivientes a navegar y acceder a servicios a través de defensa directa y relaciones de referencia de confianza. Con financiamiento ampliado, podemos aumentar la asistencia de emergencia, construir capacidad profesional interna, crear programas laborales coordinados y, finalmente, ofrecer servicios de vivienda, legales, de salud mental y de defensa hospitalaria directamente.",
    priorities: [
      { label: "Asistencia de Emergencia para Sobrevivientes", detail: "Transporte, artículos esenciales, documentos de reemplazo, ropa apropiada para el trabajo." },
      { label: "Desarrollo Laboral", detail: "Preparación laboral, rutas de hospitalidad/ServSafe, coaching profesional, conexiones con empleadores." },
      { label: "Ferias de Recursos Comunitarios", detail: "Conectar a sobrevivientes y miembros de la comunidad con proveedores verificados en un solo lugar." },
      { label: "Transporte", detail: "Viajes a refugios, tribunales, empleo, capacitación y servicios esenciales." },
      { label: "Alojamiento Temporal de Emergencia", detail: "Cuando no hay camas de refugio disponibles y los fondos lo permiten — aprobado caso por caso." },
      { label: "Personal y Capacidad de Gestión de Casos", detail: "Para poder acompañar a los sobrevivientes a través de referencias, listas de espera y citas." },
      { label: "Servicios Internos de Salud Mental", detail: "Llevar la consejería informada sobre el trauma directamente a The Pillar Project." },
      { label: "Servicios Legales Internos", detail: "Abogados centrados en el sobreviviente para órdenes de protección, derecho familiar, trata y agresión sexual." },
      { label: "Capacitación Profesional", detail: "Educación informada sobre el trauma para policía, hospitales y grupos comunitarios en Maryland." },
      { label: "Desarrollo de Defensa Hospitalaria", detail: "Construir el modelo de defensores capacitados en hospitales para intervenir en el punto de atención." },
      { label: "Datos y Medición de Impacto", detail: "Seguimiento riguroso de resultados para que financiadores y socios vean la diferencia que hace su apoyo." },
      { label: "Vivienda Transitoria y Desarrollo de Refugio", detail: "Construir la capacidad de vivienda propia de The Pillar Project con el tiempo." },
    ],
    waysTitle: "Formas de Colaborar",
    ways: [
      { title: "Colaboración de Subvención", desc: "Inversiones plurianuales en empleo, defensa hospitalaria o servicios integrados." },
      { title: "Patrocinio Corporativo", desc: "Financia capacitaciones, ferias de recursos o cohortes laborales." },
      { title: "Fondo de Emergencia para Sobrevivientes", desc: "Contribuciones directas para transporte, alojamiento y artículos esenciales." },
      { title: "Patrocinio de Programa", desc: "Patrocina una serie de capacitaciones, cohorte laboral o evento de feria de recursos." },
    ],
    ctaTitle: "Hablemos de Lo Que Es Posible.",
    ctaText: "Las colaboraciones de subvenciones, patrocinios corporativos, apoyo en especie y compromisos plurianuales impulsan a The Pillar Project hacia adelante.",
    ctaSponsorship: "Solicitar Paquete de Patrocinio",
    ctaGrant: "Conversar Sobre una Colaboración",
    ctaDonate: "Donar Ahora",
  },
};

const ICONS = [HandHeart, Building2, Heart, Users];

export default function FundersPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;

  return (
    <div>
      <section className="bg-warm-800 text-white py-16 md:py-24" data-testid="funders-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-300 mb-4 block">{c.overline}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }} data-testid="funders-title">
              {c.title}
            </h1>
            <p className="text-lg text-warm-200 leading-relaxed max-w-2xl">{c.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10 max-w-3xl">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-6 h-6 text-warm-700" />
              <h2 className="text-3xl sm:text-4xl font-medium text-gray-900" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.prioritiesTitle}</h2>
            </div>
            <p className="text-gray-600">{c.prioritiesIntro}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.priorities.map((p, idx) => (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.03 }}
                className="bg-white border border-gray-100 rounded-xl p-5"
                data-testid={`funders-priority-${idx}`}
              >
                <p className="font-semibold text-gray-900 mb-1 text-sm" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{p.label}</p>
                <p className="text-xs text-gray-600 leading-relaxed">{p.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-warm-50 border-y border-warm-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-8 text-center" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.waysTitle}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.ways.map((w, idx) => {
              const Icon = ICONS[idx];
              return (
                <div key={w.title} className="bg-white rounded-xl border border-warm-100 p-6">
                  <Icon className="w-7 h-7 text-warm-700 mb-3" />
                  <p className="font-semibold text-gray-900 mb-1" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{w.title}</p>
                  <p className="text-sm text-gray-600">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-14 bg-warm-800 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-medium mb-4" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.ctaTitle}</h2>
          <p className="text-warm-200 mb-6">{c.ctaText}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="mailto:director@thepillarprojectbaltimore.org?subject=Sponsorship%20Package%20Request">
              <Button className="bg-white text-warm-800 hover:bg-warm-100 rounded-full px-6 py-5 font-semibold" data-testid="funders-sponsorship">
                {c.ctaSponsorship} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="mailto:director@thepillarprojectbaltimore.org?subject=Grant%20Partnership%20Discussion">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-6 py-5" data-testid="funders-grant">{c.ctaGrant}</Button>
            </a>
            <Link to="/donate">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-6 py-5" data-testid="funders-donate">{c.ctaDonate}</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
